import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Award, Shield, CheckCircle, Star, Globe, Users } from 'lucide-react';
import TechnologyPartnersCarousel from './TechnologyPartnersCarousel';

const CertificationsSection = styled.section`
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
`;

const CertificationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00ff41, #00ffff, #ff0080);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 80px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 60px;
  }
`;

const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const CertificationCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 40px 30px;
  border-radius: 25px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(135,206,235,0.1), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(135,206,235,0.6);
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    
    &::before {
      left: 100%;
    }
  }
`;

const CertificationIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  color: white;
  box-shadow: 0 10px 30px rgba(135,206,235,0.3);
`;

const CertificationTitle = styled.h3`
  font-size: 1.4rem;
  color: #87ceeb;
  margin-bottom: 15px;
  font-weight: 700;
`;

const CertificationDescription = styled.p`
  color: rgba(255,255,255,0.9);
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 20px;
`;

const CertificationBadge = styled.div`
  background: linear-gradient(135deg, #00ff41 0%, #00ffff 100%);
  color: #000;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
`;

const PartnershipsSection = styled.div`
  margin-top: 80px;
  text-align: center;
`;

const PartnershipsTitle = styled.h3`
  font-size: 2rem;
  color: #87ceeb;
  margin-bottom: 40px;
  font-weight: 700;
`;

const PartnershipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const PartnerCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 30px 20px;
  border-radius: 15px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(135,206,235,0.5);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
`;

const PartnerLogo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
`;

const PartnerName = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 1rem;
`;

const CertificationsAndPartnerships = () => {
  const certifications = [
    {
      icon: <Award size={50} />,
      title: 'ISO 27001 Certified',
      description: 'Information Security Management System certification ensuring the highest standards of data protection and security.',
      badge: 'Security Certified'
    },
    {
      icon: <Shield size={50} />,
      title: 'GDPR Compliant',
      description: 'Full compliance with General Data Protection Regulation for handling European customer data and privacy.',
      badge: 'Privacy Compliant'
    },
    {
      icon: <CheckCircle size={50} />,
      title: 'MSME Registered',
      description: 'Officially registered Micro, Small & Medium Enterprise with Government of India recognition.',
      badge: 'Government Recognized'
    },
    {
      icon: <Star size={50} />,
      title: 'Quality Assurance',
      description: 'Certified quality management processes ensuring consistent delivery of high-standard solutions.',
      badge: 'Quality Certified'
    },
    {
      icon: <Globe size={50} />,
      title: 'International Standards',
      description: 'Adherence to international coding standards and best practices for global project delivery.',
      badge: 'Global Standards'
    },
    {
      icon: <Users size={50} />,
      title: 'Agile Certified',
      description: 'Certified Agile and Scrum methodologies for efficient project management and delivery.',
      badge: 'Agile Certified'
    }
  ];

  const partners = [
    { name: 'AWS', logo: 'AWS' },
    { name: 'Google Cloud', logo: 'GCP' },
    { name: 'Microsoft Azure', logo: 'Azure' },
    { name: 'MongoDB', logo: 'Mongo' },
    { name: 'React', logo: 'React' },
    { name: 'Node.js', logo: 'Node' }
  ];

  return (
    <CertificationsSection>
      <CertificationsContainer>
        <SectionTitle>Certifications & Standards</SectionTitle>
        <SectionSubtitle>
          Trusted certifications and compliance standards that ensure quality, security, and reliability in all our services
        </SectionSubtitle>
        
        <CertificationsGrid>
          {certifications.map((cert, index) => (
            <CertificationCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <CertificationIcon>
                {cert.icon}
              </CertificationIcon>
              <CertificationTitle>{cert.title}</CertificationTitle>
              <CertificationDescription>{cert.description}</CertificationDescription>
              <CertificationBadge>{cert.badge}</CertificationBadge>
            </CertificationCard>
          ))}
        </CertificationsGrid>

        <PartnershipsSection>
          <PartnershipsTitle>Technology Partners</PartnershipsTitle>
          <TechnologyPartnersCarousel />
        </PartnershipsSection>
      </CertificationsContainer>
    </CertificationsSection>
  );
};

export default CertificationsAndPartnerships;